import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { useTheme } from "./ThemeProvider";
import { getSiteSettings } from "@/lib/api";
import logo from "@/assets/logo.png";

const Navbar = () => {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { resolved, setTheme } = useTheme();
  const [logoLight, setLogoLight] = useState<string | null>(null);
  const [logoDark, setLogoDark] = useState<string | null>(null);
  const [logoHeightDesktop, setLogoHeightDesktop] = useState<number>(64);
  const [logoHeightMobile, setLogoHeightMobile] = useState<number>(48);
  const [isDesktop, setIsDesktop] = useState<boolean>(
    typeof window !== "undefined" ? window.matchMedia("(min-width: 768px)").matches : true
  );

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  useEffect(() => {
    getSiteSettings().then((s) => {
      setLogoLight(s.site_logo_url || null);
      setLogoDark(s.site_logo_dark_url || s.site_logo_url || null);
      const d = Number(s.site_logo_height_desktop);
      const m = Number(s.site_logo_height_mobile);
      if (d && d >= 24 && d <= 160) setLogoHeightDesktop(d);
      if (m && m >= 20 && m <= 120) setLogoHeightMobile(m);
      if (s.site_favicon_url) {
        let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement | null;
        if (!link) {
          link = document.createElement("link");
          link.rel = "icon";
          document.head.appendChild(link);
        }
        link.href = s.site_favicon_url;
      }
    }).catch(() => {});
  }, []);

  const activeLogo = resolved === "dark" ? (logoDark || logoLight) : (logoLight || logoDark);
  const useCustomLogo = !!activeLogo;

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/domains", label: "Domains" },
    { to: "/sell-domain", label: "Sell Your Domain" },
    { to: "/sales", label: "Sales" },
    
    { to: "/blog", label: "Blog" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  const toggleTheme = () => setTheme(resolved === "dark" ? "light" : "dark");

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border/50 bg-background/60 backdrop-blur-xl">
      <div className="container flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <img
            src={activeLogo || logo}
            alt="PDNInvestor"
            width={256}
            height={256}
            fetchPriority="high"
            decoding="async"
            style={{ height: `${isDesktop ? logoHeightDesktop : logoHeightMobile}px`, width: "auto" }}
            className={`w-auto ${useCustomLogo ? "" : "dark:invert"}`}
          />
        </Link>


        <div className="hidden lg:flex items-center gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`text-sm font-medium transition-colors duration-200 hover:text-foreground ${
                location.pathname === link.to ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-md text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Toggle theme"
          >
            {resolved === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            className="lg:hidden text-foreground"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-border/50 bg-background/95 backdrop-blur-xl px-6 py-4 space-y-3">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMobileOpen(false)}
              className={`block text-sm font-medium transition-colors ${
                location.pathname === link.to ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
